export const decodeDocumentCasePolicySegment = (rawSegment) => {
    if (typeof rawSegment !== 'string' || !rawSegment || rawSegment.length > 1024) return null;
    let value = rawSegment;
    for (let index = 0; index < 2 && value.includes('%'); index += 1) {
        try {
            const decoded = decodeURIComponent(value.replace(/%(?![0-9a-f]{2})/gi, '%25'));
            if (decoded === value) break;
            value = decoded;
        } catch (error) {
            return null;
        }
    }
    if (value.includes('%')) {
        try {
            if (decodeURIComponent(value.replace(/%(?![0-9a-f]{2})/gi, '%25')) !== value) return null;
        } catch (error) {
            return null;
        }
    }
    if (!value || /[\\\u0000-\u001f\u007f]/.test(value)) return null;
    return value;
};

const documentPolicySegments = (rawSegments) => rawSegments
    .map(decodeDocumentCasePolicySegment)
    .flatMap(segment => segment === null ? [null] : segment.split('/'));

const documentPolicySegmentsAllowed = (segments) => segments.every((segment, index) => {
    const normalized = String(segment).trim().toLowerCase();
    return segment !== null
        && segment !== ''
        && segment !== '.'
        && segment !== '..'
        && (index === 0 ? segment === '{{Name}}' : segment !== '{{Name}}')
        && !(index === 1 && normalized === 'leads')
        && !['pre-case lead documents', 'lead documents'].includes(normalized);
});

export const isDocumentCaseLogicalPathAllowed = (rawPath) => {
    if (typeof rawPath !== 'string' || !rawPath || rawPath.length > 2048
        || /[\\\u0000-\u001f\u007f]/.test(rawPath)) {
        return false;
    }
    const rawSegments = rawPath.split('/');
    return rawSegments[0] === '{{Name}}'
        && rawSegments.length <= 100
        && documentPolicySegmentsAllowed(documentPolicySegments(rawSegments));
};

export const parseDocumentCaseStorageKey = (rawKey, { folder = false } = {}) => {
    if (typeof rawKey !== 'string' || !rawKey || rawKey.length > 4096) return null;
    const trailingMatch = rawKey.match(/\/+$/);
    if ((trailingMatch?.[0]?.length || 0) > 1) return null;
    const hasTrailingSeparator = rawKey.endsWith('/');
    const key = hasTrailingSeparator ? rawKey.slice(0, -1) : rawKey;
    const segments = key.split('/');
    if (segments.length < 4 || segments[0] !== 'cases'
        || !/^[1-9][0-9]*$/.test(segments[1]) || segments[2] !== '{{Name}}'
        || segments.some(segment => segment === '')) {
        return null;
    }
    const logicalSegments = segments.slice(2);
    const directorySegments = folder ? logicalSegments : logicalSegments.slice(0, -1);
    if (!documentPolicySegmentsAllowed(documentPolicySegments(directorySegments))) return null;

    if (!folder) {
        const decodedFile = decodeDocumentCasePolicySegment(logicalSegments[logicalSegments.length - 1]);
        const fileParts = decodedFile === null ? [] : decodedFile.split('/');
        if (decodedFile === null || fileParts.some(part => !part || part === '.' || part === '..')) {
            return null;
        }
        const decodedDirectoryCount = documentPolicySegments(directorySegments).length;
        if (fileParts.slice(0, -1).some((part, index) => {
            const normalized = part.trim().toLowerCase();
            return (decodedDirectoryCount === 1 && index === 0 && normalized === 'leads')
                || ['pre-case lead documents', 'lead documents'].includes(normalized);
        })) return null;
    }

    return {
        logicalPath: logicalSegments.join('/'),
        logicalParentPath: logicalSegments.slice(0, -1).join('/'),
        hasTrailingSeparator,
        name: logicalSegments[logicalSegments.length - 1]
    };
};

export const isDocumentCaseStorageItemAllowed = (item, parentPath) => {
    if (!item || typeof item !== 'object' || !isDocumentCaseLogicalPathAllowed(parentPath)) {
        return false;
    }
    const rawKey = String(item.key || item.file_path || '');
    const isFolder = item.type === 'folder' || item.is_folder === true || rawKey.endsWith('/');
    const parsed = parseDocumentCaseStorageKey(rawKey, { folder: isFolder });
    if (!parsed) return false;
    if (item.name === '(Folder)' && isFolder) return parsed.logicalPath === parentPath;
    if (isFolder) {
        const expected = `${parentPath}/${String(item.name || '')}`;
        return Boolean(item.name)
            && isDocumentCaseLogicalPathAllowed(expected)
            && parsed.logicalPath === expected;
    }
    return parsed.logicalParentPath === parentPath;
};

export const normalizeDocumentSyncFolderPath = (rawPath) => {
    if (typeof rawPath !== 'string' || rawPath.length === 0) return null;
    return rawPath.endsWith('/') ? rawPath : `${rawPath}/`;
};

export const normalizeDocumentSyncMatchPath = (rawPath) => (
    typeof rawPath === 'string' && rawPath.length > 0 ? rawPath : null
);
